import { ReactComponent as AdIcon } from 'img/icons/subscriptions/ad-icon.svg';
import { ReactComponent as AdIconBlue } from 'img/icons/subscriptions/ad-icon-blue.svg';
import { ReactComponent as AdditionalSpaceIcon } from 'img/icons/subscriptions/additional-space.svg';
import { ReactComponent as AvailableSpaceIcon } from 'img/icons/subscriptions/available-space-icon.svg';
import { ReactComponent as BigFilesIcon } from 'img/icons/subscriptions/big-files.svg';
import { ReactComponent as FamilyIcon } from 'img/icons/subscriptions/family.svg';
import { ReactComponent as FilesIcon } from 'img/icons/subscriptions/files-icon.svg';
import { ReactComponent as MailIcon } from 'img/icons/subscriptions/mail-icon.svg';
import { ReactComponent as PublicPasswordIcon } from 'img/icons/subscriptions/public-password.svg';
import { ReactComponent as SupportIconBlue } from 'img/icons/subscriptions/support-blue.svg';
import { ReactComponent as SupportIcon } from 'img/icons/subscriptions/support-icon.svg';
import { ReactComponent as ThemedPublicIcon } from 'img/icons/subscriptions/themed-public.svg';
import { ReactComponent as TimerIcon } from 'img/icons/subscriptions/timer.svg';
import { ReactComponent as UnlimitedUploadIcon } from 'img/icons/subscriptions/unlimited-upload.svg';
import React, { forwardRef } from 'react';
import { updatedUnsubScreensEnabled } from 'reactApp/appHelpers/featuresHelpers/features/overquotaNewUnsub';
import {
    type SubscriptionFeatures,
    useGetPaidFeatures,
} from 'reactApp/components/CancelAutoRenewConfirmDialog/CancelAutoRenewConfirmDialog';
import { AUTOUPLOAD_10TB_REGEX } from 'reactApp/modules/products/products.helpers';

import styles from './PrivilegeList.css';

const iconProps = {
    width: 32,
    height: 32,
} as const;

let privileges = [
    // tempexp_16460-next-line
    {
        Icon: AvailableSpaceIcon,
        getText: (s: string, productId: string) => {
            if (AUTOUPLOAD_10TB_REGEX.test(productId)) {
                return `${s} тарифа и 10 ТБ на автозагрузку`;
            }

            return `${s} в Почте и Облаке`;
        },
    },
    { Icon: AdIcon, getText: () => 'Отключение рекламы' },
    { Icon: FilesIcon, getText: () => 'Возможность сохранять в Облаке файлы более 2 ГБ' },
    { Icon: MailIcon, getText: () => 'Возможность отправлять файлы более 2 ГБ' },
    { Icon: SupportIcon, getText: () => 'Приоритетную поддержку' },
];

const privilegesAutoUpload = [
    {
        Icon: AvailableSpaceIcon,
        getText: () => 'Автозагрузка будет занимать место',
    },
    { Icon: AdIcon, getText: () => 'Вернётся реклама — даже в Почте' },
    { Icon: FilesIcon, getText: () => 'Не загрузятся файлы от 2 ГБ' },
    { Icon: MailIcon, getText: () => 'Не отправятся файлы больше 2 ГБ' },
    { Icon: SupportIcon, getText: () => 'Не будет приоритетной поддержки' },
] as const;

const mailFeatures = (features: SubscriptionFeatures) =>
    [
        { Icon: AdIconBlue, text: 'Без рекламы', check: features.disableAds },
        { Icon: BigFilesIcon, text: 'Загрузка вложений весом до 100 ГБ', check: true },
    ] as PaidFeature[];

const cloudFeatures = (features: SubscriptionFeatures, count: number) =>
    [
        { Icon: AdditionalSpaceIcon, text: 'Дополнительное место для писем и файлов', check: true },
        { Icon: UnlimitedUploadIcon, text: 'Безлимитная автозагрузка фото\nи видео с телефона', check: features.unlimitedUpload },
        { Icon: AdIconBlue, text: 'Без рекламы', check: features.disableAds },
        {
            Icon: BigFilesIcon,
            text: `Загрузка файлов${count < 2 ? ' и вложений' : ''} весом до\u00A0100\u00A0ГБ${count < 2 ? ' в Почте и Облаке' : ''}`,
            check: true,
        },
        { Icon: FamilyIcon, text: 'Возможность делиться памятью ещё с 3 участниками', check: features.family },
        { Icon: TimerIcon, text: 'Таймер для автоудаления отправленых папок', check: features.autodelete },
        { Icon: ThemedPublicIcon, text: 'Выбор оформления совместных папок', check: features.themedPublic },
        { Icon: PublicPasswordIcon, text: 'Пароль для совместных папок в веб версии', check: features.publicPassword },
        { Icon: SupportIconBlue, text: 'Приоритетная поддержка', check: true },
    ] as PaidFeature[];

type Props = {
    currentSpace: string;
    productId: string;
    isAutoUpload?: boolean;
    showAds?: boolean;
};

type PaidFeature = { Icon: React.FC<React.SVGProps<SVGSVGElement>>; text: string; check: boolean };

// tempexp_16460-next-line
export const PrivilegeList = forwardRef<HTMLDivElement, Props>(({ currentSpace, productId, isAutoUpload, showAds }, ref) => {
    if (showAds) {
        privileges = privileges.filter((item) => item.Icon !== AdIcon);
    }
    const features = useGetPaidFeatures();

    if (updatedUnsubScreensEnabled) {
        const mailPrivileges = mailFeatures(features);
        const count = mailPrivileges.filter((item) => item.check).length;
        const cloudPrivileges = cloudFeatures(features, count);

        return (
            <div className={styles.scrollable_container}>
                {count < 2 && (
                    <div className={styles.features_container} ref={ref}>
                        {cloudPrivileges.map(
                            ({ Icon, text, check }, idx) =>
                                check && (
                                    <div key={idx} className={styles.item}>
                                        <div className={styles.icon}>
                                            <Icon {...iconProps} />
                                        </div>
                                        <span>{text}</span>
                                    </div>
                                )
                        )}
                    </div>
                )}
                {count >= 2 && (
                    <>
                        <div className={styles.features_container} ref={ref}>
                            <div className={styles.title}>В Почте</div>
                            <div className={styles.features_list}>
                                {mailPrivileges.map(
                                    ({ Icon, text, check }, idx) =>
                                        check && (
                                            <div key={idx} className={styles.item}>
                                                <div className={styles.icon}>
                                                    <Icon {...iconProps} />
                                                </div>
                                                <span>{text}</span>
                                            </div>
                                        )
                                )}
                            </div>
                        </div>
                        <div className={styles.features_container} ref={ref}>
                            <div className={styles.title}>В Облаке</div>
                            <div className={styles.features_list}>
                                {cloudPrivileges.map(
                                    ({ Icon, text, check }, idx) =>
                                        check && (
                                            <div key={idx} className={styles.item}>
                                                <div className={styles.icon}>
                                                    <Icon {...iconProps} />
                                                </div>
                                                <span>{text}</span>
                                            </div>
                                        )
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    }

    return (
        <div className={styles.root} ref={ref}>
            {(isAutoUpload ? privilegesAutoUpload : privileges).map(({ Icon, getText }, idx) => (
                <div key={idx} className={styles.item}>
                    <div className={styles.icon}>
                        <Icon {...iconProps} />
                    </div>
                    <span>{getText(currentSpace, productId)}</span>
                </div>
            ))}
        </div>
    );
});

PrivilegeList.displayName = 'PrivilegeList';
